import {
  AuthError,
  ExpiredSessionError,
  NotFoundError,
  ServerError,
  SilentError,
  UnknownError,
} from '../../client/error/errors';
import { BaseResponse } from '@yleisradio/areena-types';

/**
 * Handles areena-api specific errors from fetchers
 * @param config
 * @returns {Promise<ResponseType>}
 */
export async function areenaApiErrorHandler(config: {
  response: Response;
  url: string;
  ignoreBody: true;
  isUserLoggedIn?: boolean;
  silentlyConsumedStatusCodes?: number[];
}): Promise<void>;
export async function areenaApiErrorHandler<
  ResponseType extends BaseResponse,
>(config: {
  response: Response;
  url: string;
  ignoreBody?: boolean;
  isUserLoggedIn?: boolean;
  silentlyConsumedStatusCodes?: number[];
}): Promise<ResponseType | void>;
export async function areenaApiErrorHandler<
  ResponseType extends BaseResponse,
>(config: {
  response: Response;
  url: string;
  ignoreBody?: boolean;
  isUserLoggedIn?: boolean;
  silentlyConsumedStatusCodes?: number[];
}): Promise<ResponseType | void> {
  const {
    response,
    url,
    ignoreBody = false,
    isUserLoggedIn = false,
    silentlyConsumedStatusCodes,
  } = config;

  const silentError = !!silentlyConsumedStatusCodes?.includes(response.status);

  let errorMessage: string = '';

  if (response.status === 401) {
    if (isUserLoggedIn) {
      console.error(`Expired session ${url} ${response.status}`);
      errorMessage = 'Expired session';
      if (!silentError) throw new ExpiredSessionError(errorMessage);
    } else {
      console.error(`Authentication failure ${url} ${response.status}`);
      errorMessage = 'Authentication failure';
      if (!silentError) throw new AuthError(errorMessage);
    }
  }
  if (response.status === 404) {
    console.error(`Not found ${url} ${response.status}`);
    errorMessage = 'Not found';
    if (!silentError) throw new NotFoundError(errorMessage);
  }

  if (response.status === 400) {
    const data = (await response.json()) as ResponseType;
    const notifications = data.notifications
      ?.filter(notifications => !!notifications.developerMessage)
      .reduce(
        (acc, current) => acc.concat(`${current?.developerMessage} `),
        ''
      );
    console.error(
      `Failed to fetch ${url}, developerMessages: ${notifications}`
    );
    errorMessage = 'Expired session';
    if (!silentError) throw new ExpiredSessionError(errorMessage);
  }

  if (response.status >= 500) {
    errorMessage = `Internal Server error: ${response.status} occurred while fetching: ${url}`;
    console.error(errorMessage);
    if (!silentError) throw new ServerError(errorMessage);
  }

  if (silentError) {
    throw new SilentError(
      `Consumed following error silently: \n"${errorMessage}"`
    );
  }

  if (response.status !== 200) {
    console.error(`Failed to fetch ${url} ${response.status}`);
    if (!silentError)
      throw new UnknownError(`Failed to fetch ${url} ${response.status}`);
  }

  const responseData = ignoreBody
    ? undefined
    : ((await response.json()) as ResponseType);

  if (responseData?.notifications) {
    responseData.notifications
      .filter(notification => !!notification.developerMessage)
      .forEach(notification => {
        console.info(
          `API responded with a notification: ${notification.developerMessage}`
        );
      });
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }

  return responseData;
}
